import { v4 as uuidv4 } from 'uuid';
import { createFakeContact } from './contactsData';

// Collection of realistic company logos
const companyLogos = [
    'https://via.placeholder.com/150/0077B5/FFFFFF?text=EE',
    'https://via.placeholder.com/150/FF0000/FFFFFF?text=SP',
    'https://via.placeholder.com/150/4CAF50/FFFFFF?text=HE',
    'https://via.placeholder.com/150/FFC107/FFFFFF?text=MS',
    'https://via.placeholder.com/150/9C27B0/FFFFFF?text=VM',
    'https://via.placeholder.com/150/03A9F4/FFFFFF?text=SS',
    'https://via.placeholder.com/150/E91E63/FFFFFF?text=EE',
    'https://via.placeholder.com/150/795548/FFFFFF?text=AA',
];

// Company names with corresponding types
const companyData = [
    { name: 'Elite Entertainment', type: 'Production Company' },
    { name: 'Sunset Productions', type: 'Event Management' },
    { name: 'Harmony Events', type: 'Corporate Events' },
    { name: 'MasterSound Ltd', type: 'Audio Services' },
    { name: 'Visionary Media Group', type: 'Media Production' },
    { name: 'Spotlight Solutions', type: 'Lighting Services' },
    { name: 'Apex Audio Visual', type: 'AV Equipment' },
    { name: 'Stellar Events Co', type: 'Event Planning' },
    { name: 'Creative Concepts', type: 'Design Agency' },
    { name: 'Premier Productions', type: 'Video Production' },
    { name: 'Soundwave Audio', type: 'Sound Engineering' },
    { name: 'Eventful Experiences', type: 'Event Management' },
    { name: 'LightWorks Pro', type: 'Lighting Design' },
    { name: 'MediaMagic Studios', type: 'Media Production' },
    { name: 'Tech Trends AV', type: 'Audio Visual Services' }
];

// Domain names for email
const domains = [
    'eliteentertainment.com',
    'sunsetproductions.co.uk',
    'harmonyevents.com',
    'mastersound.co.uk',
    'visionarymedia.com',
    'spotlightsolutions.co.uk',
    'apexav.com',
    'stellarevents.co.uk',
    'creative-concepts.com',
    'premierproductions.co.uk',
    'soundwaveaudio.com',
    'eventful.co.uk',
    'lightworkspro.com',
    'mediamagic.co.uk',
    'techtrendsav.com'
];

// Generate realistic phone numbers
const generatePhoneNumber = () => {
    return `+44 (0)${Math.floor(Math.random() * 1000)}-${Math.floor(Math.random() * 1000)}-${Math.floor(Math.random() * 10000)}`.replace(/\b(\d{1})\b/g, '0$1');
};

// Generate a random date within the last year
const generateRecentDate = () => {
    const now = new Date();
    const pastDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    const randomTime = pastDate.getTime() + Math.random() * (now.getTime() - pastDate.getTime());
    return new Date(randomTime).toISOString().slice(0, 10);
};

// Generate document types
const documentTypes = ['Contract', 'Invoice', 'Proposal', 'Service Agreement', 'Insurance', 'Tax Document', 'Statement of Work', 'Terms & Conditions'];

// Generate random documents
const generateDocuments = (count = 3) => {
    const documents = [];
    const documentNames = [
        'Service Contract',
        'Master Agreement',
        'Equipment Rental Terms',
        'Insurance Certificate',
        'Tax Registration',
        'Company Profile',
        'Rate Card',
        'Terms of Service',
        'Company Overview',
        'Partnership Agreement'
    ];

    for (let i = 0; i < Math.min(count, 10); i++) {
        documents.push({
            id: uuidv4(),
            name: documentNames[Math.floor(Math.random() * documentNames.length)],
            date: generateRecentDate(),
            type: documentTypes[Math.floor(Math.random() * documentTypes.length)]
        });
    }

    return documents;
};

// Generate staff members
const generateStaffMembers = (count = 4, companyName) => {
    const staff = [];
    for (let i = 0; i < count; i++) {
        const staffMember = createFakeContact();
        staffMember.company = companyName; // Make sure company name is consistent
        staff.push(staffMember);
    }
    return staff;
};

// Generate address data
const generateAddress = () => {
    const streets = ['Business Park', 'Industrial Estate', 'Commercial Road', 'Enterprise Way', 'Technology Drive', 'Media City', 'Office Park', 'Corporate Boulevard'];
    const towns = ['London', 'Manchester', 'Birmingham', 'Liverpool', 'Glasgow', 'Edinburgh', 'Bristol', 'Leeds'];
    const counties = ['Greater London', 'Greater Manchester', 'West Midlands', 'Merseyside', 'Lanarkshire', 'Edinburgh', 'Avon', 'West Yorkshire'];
    const postcodes = ['SW1A 1AA', 'M1 1AA', 'B1 1AA', 'L1 1AA', 'G1 1AA', 'EH1 1AA', 'BS1 1AA', 'LS1 1AA'];

    return {
        address1: `Unit ${Math.floor(Math.random() * 100) + 1}, ${streets[Math.floor(Math.random() * streets.length)]}`,
        address2: Math.random() > 0.5 ? `Building ${String.fromCharCode(65 + Math.floor(Math.random() * 26))}` : '',
        town: towns[Math.floor(Math.random() * towns.length)],
        county: counties[Math.floor(Math.random() * counties.length)],
        postcode: postcodes[Math.floor(Math.random() * postcodes.length)],
        country: 'United Kingdom'
    };
};

// Generate company website based on name
const generateWebsite = (companyName) => {
    return `www.${companyName.toLowerCase().replace(/\s+/g, '')}.${Math.random() > 0.5 ? 'com' : 'co.uk'}`;
};

// Generate company registration number
const generateRegNumber = () => {
    return `${Math.floor(Math.random() * 10000000) + 10000000}`;
};

// Create a fake company
export const createFakeCompany = (id = null) => {
    const companyIndex = Math.floor(Math.random() * companyData.length);
    const companyName = companyData[companyIndex].name;
    const companyType = companyData[companyIndex].type;
    const domain = domains[companyIndex % domains.length];
    const email = `info@${domain}`;

    return {
        id: id || uuidv4(),
        name: companyName,
        type: companyType,
        number: generatePhoneNumber(),
        email,
        website: generateWebsite(companyName),
        regNumber: generateRegNumber(),
        description: `${companyName} specializes in providing ${companyType.toLowerCase()} services to the events and entertainment industry.`,
        image: companyLogos[Math.floor(Math.random() * companyLogos.length)],
        isBookable: Math.random() > 0.4,
        isVat: Math.random() > 0.3,
        documents: generateDocuments(Math.floor(Math.random() * 4) + 1),
        staff: generateStaffMembers(Math.floor(Math.random() * 5) + 1, companyName),
        address: generateAddress()
    };
};

// Generate multiple fake companies
export const generateFakeCompanies = (count = 10) => {
    const companies = [];
    for (let i = 0; i < count; i++) {
        companies.push(createFakeCompany(i + 1));
    }
    return companies;
}; 